<script setup>
  import { ref, computed, watch, onUnmounted } from "vue";

  import BaseCombobox from "./shared/base-combobox.vue";
  import WrapWithHint from "./wrap-with-hint.vue";
  import { useOpenLotCodeSearch } from "./stores/open-lot-code-search-store";
  import { Flash } from "~/jose_modern/flash";
  import { quantityLessOnShipmentWithUnit } from "~/jose_modern/quantity_with_unit";

  // VueUse's useDebounceFn returns neither `.cancel` nor `.flush`, so a pending call
  // can't be stopped -- settling to the seed must be able to drop an in-flight
  // keystroke, and a blur must be able to run one without waiting out the delay.
  function debounce(fn, delay) {
    let timer = null;
    let pendingArgs = null;

    function debounced(...args) {
      clearTimeout(timer);
      pendingArgs = args;
      timer = setTimeout(() => {
        timer = null;
        fn(...pendingArgs);
      }, delay);
    }
    debounced.cancel = () => {
      clearTimeout(timer);
      timer = null;
      pendingArgs = null;
    };
    debounced.flush = () => {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      fn(...pendingArgs);
    };
    return debounced;
  }

  const props = defineProps({
    modelValue: { type: [String, Number], default: "" },
    items: { type: Array, default: () => [] },
    sourceUrl: { type: String, default: "" },
    seed: { type: Array, default: () => [] },
    seedTotal: { type: Number, default: 0 },
    searchParams: { type: Object, default: () => ({}) },
    searchRemotely: { type: Boolean, default: false },
    unit: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    disabledValues: { type: Array, default: () => [] },
    inUseValues: { type: Array, default: () => [] },
    inUseLabel: { type: String, default: "In use" },
    allowCreate: { type: Boolean, default: true },
    hasError: { type: Boolean, default: false },
    placeholder: { type: String, default: "Lot code" },
    inputClass: { type: [String, Object, Array], default: "" },
    newLotHint: { type: String, default: "" },
    isNew: { type: Boolean, default: false },
    isNegative: { type: Boolean, default: false },
    negativeHint: { type: String, default: "" },
    isDuplicate: { type: Boolean, default: false },
    duplicateHint: { type: String, default: "" },
    isRequired: { type: Boolean, default: false },
    requiredHint: { type: String, default: "" },
  });

  const showNegativeBadge = computed(() => props.isNegative && !props.isNew && !props.isDuplicate);
  const hasBadge = computed(
    () => props.isRequired || props.isNew || props.isDuplicate || showNegativeBadge.value
  );

  const emit = defineEmits(["select", "create", "clear"]);

  const CREATE_VALUE = "__create_lot__";

  const SEARCH_ERROR =
    "Sorry, there was an error with your search. Please try again or contact support.";

  const searchTerm = ref("");
  const searchFailed = ref(false);

  const {
    remote,
    results,
    totalEntries,
    loading,
    search: searchRequest,
    showSeed: showSeedResults,
    abort: abortSearchRequest,
    clear: clearSearch,
  } = useOpenLotCodeSearch({
    sourceUrl: props.sourceUrl,
    seed: props.seed,
    seedTotal: props.seedTotal,
    searchParams: () => props.searchParams,
    searchRemotely: props.searchRemotely,
  });

  // The term the shown results -- or the ones on their way -- belong to.
  let requestedTerm = null;
  let pendingSearch = null;

  function search(query) {
    pendingSearch = runSearch(query, () => searchRequest(query));
    return pendingSearch;
  }

  async function runSearch(query, startRequest) {
    requestedTerm = query;

    try {
      await startRequest();
      searchFailed.value = false;
    } catch {
      if (requestedTerm === query) requestedTerm = null;
      searchFailed.value = true;
      Flash.error(SEARCH_ERROR);
    }
  }

  const debouncedSearch = debounce((query) => search(query), 300);

  async function settleSearch() {
    debouncedSearch.flush();
    await pendingSearch;
  }

  function abortSearch() {
    debouncedSearch.cancel();
    abortSearchRequest();
  }

  // The server ships this row's first page of open lots inline, so an untouched
  // field can show them straight away rather than waiting on a fetch. This is the
  // settled state, so drop any pending search and its loading indicator.
  //
  // Opening from the caret both clears the term and focuses the field, and either
  // alone lands here -- settling twice would abort the first request mid-flight.
  function showSeed() {
    if (requestedTerm === "") return;

    abortSearch();
    pendingSearch = runSearch("", showSeedResults);
  }

  // Stale rows would misrepresent the new term, so the list switches to
  // "Loading..." while the dropdown itself stays open.
  function startLoading() {
    requestedTerm = null;
    loading.value = true;
    results.value = [];
    totalEntries.value = 0;
  }

  function round(value) {
    return Number(value.toFixed(6));
  }

  const serverOptions = computed(() =>
    results.value.map((plc) => {
      const displayQuantity =
        plc.remaining_quantity == null ? null : Number(plc.remaining_quantity);
      return {
        value: plc.id,
        persisted_lot_code_id: plc.id,
        lot_code: plc.lot_code,
        date: plc.date ?? null,
        remaining_in_unit:
          plc.remaining_quantity_units == null ? null : Number(plc.remaining_quantity_units),
        on_shipment_in_unit: Number(plc.on_shipment_quantity_units ?? 0),
        remaining:
          displayQuantity == null
            ? null
            : quantityLessOnShipmentWithUnit(
                round(displayQuantity),
                round(Number(plc.on_shipment_quantity ?? 0)),
                plc.remaining_unit ?? props.unit
              ),
        label: plc.label ?? plc.lot_code,
      };
    })
  );

  function inputTextFor(value) {
    const item = props.items.find((entry) => entry.value === value);
    return item?.lot_code ?? item?.label ?? "";
  }

  watch(
    () => props.modelValue,
    (value) => {
      searchTerm.value = inputTextFor(value);
    },
    { immediate: true }
  );

  const trimmedSearch = computed(() => searchTerm.value.trim());

  const selectedItem = computed(
    () => props.items.find((item) => item.value === props.modelValue) ?? null
  );

  const remainingCount = computed(() =>
    Math.max(0, totalEntries.value - serverOptions.value.length)
  );

  const displayItems = computed(() => {
    let list = serverOptions.value;
    const term = trimmedSearch.value.toLowerCase();
    const selection = selectedItem.value;

    // An untouched input self-searches the selected code, but a closed lot won't
    // be in the open-lot results -- pin it so the selection stays visible and
    // isn't offered as "Create". Once the user edits the term, the server results
    // speak for themselves.
    if (
      selection &&
      !selection.isCreate &&
      term === String(selection.lot_code ?? "").toLowerCase() &&
      !list.some((option) => option.value === selection.value)
    ) {
      list = [selection, ...list];
    }
    const hasExactMatch =
      !term ||
      list.some((item) =>
        [item.lot_code, item.label].some((field) => String(field ?? "").toLowerCase() === term)
      );
    if (hasExactMatch || loading.value || !props.allowCreate) return list;

    return [
      {
        value: CREATE_VALUE,
        lot_code: trimmedSearch.value,
        label: trimmedSearch.value,
        isCreate: true,
      },
      ...list,
    ];
  });

  function onSearchTermUpdate(value) {
    searchTerm.value = value;

    if (!value.trim()) {
      showSeed();
      return;
    }

    if (!remote) {
      search(value);
      return;
    }

    abortSearchRequest();
    startLoading();
    debouncedSearch(value);
  }

  function onFocus() {
    if (!trimmedSearch.value) {
      showSeed();
      return;
    }

    if (requestedTerm === searchTerm.value) return;

    if (remote) startLoading();
    search(searchTerm.value);
  }

  function onSelect(option) {
    if (option.isCreate) {
      emit("create", option.lot_code);
      return;
    }

    searchTerm.value = option.lot_code ?? option.label ?? "";
    emit("select", option);
  }

  function onClear() {
    searchTerm.value = "";
    emit("clear");
  }

  // Tabbing or clicking away is a choice, so the term resolves to its match or to a
  // new lot code -- you needn't come back to pick "create".
  async function onBlur() {
    const term = trimmedSearch.value;

    if (!term) {
      searchTerm.value = "";
      if (inputTextFor(props.modelValue)) emit("clear");
      return;
    }

    if (term === inputTextFor(props.modelValue)) return;

    if (loading.value) {
      await settleSearch();
      if (trimmedSearch.value !== term) return;
    }

    const lowered = term.toLowerCase();
    const pool = [...serverOptions.value, ...props.items];
    const match = pool.find((item) =>
      [item.lot_code, item.label].some((field) => String(field ?? "").toLowerCase() === lowered)
    );

    if (match) {
      onSelect(match);
    } else if (props.allowCreate) {
      emit("create", term);
    } else {
      searchTerm.value = inputTextFor(props.modelValue);
    }
  }

  onUnmounted(() => {
    debouncedSearch.cancel();
    clearSearch();
  });
</script>

<template>
  <div class="lot-code-combobox" :class="{ 'has-badge': hasBadge }">
    <BaseCombobox
      :model-value="modelValue"
      :items="displayItems"
      :search-term="searchTerm"
      :disabled="disabled"
      :disabled-values="disabledValues"
      :has-error="hasError || isRequired"
      :placeholder="placeholder"
      :input-class="inputClass"
      :loading="loading"
      :no-results-text="searchFailed ? SEARCH_ERROR : 'No open lot codes found'"
      show-no-results-when-empty
      filter-mode="external"
      @update:search-term="onSearchTermUpdate"
      @focus="onFocus"
      @select="onSelect"
      @clear="onClear"
      @blur="onBlur"
    >
      <template v-if="remainingCount > 0" #list-footer>
        <div class="combobox-more-results has-text-grey" data-test="combobox-more-results">
          {{ remainingCount }} more -- refine your search
        </div>
      </template>

      <template #option="{ option }">
        <div v-if="option.isCreate" class="lot-create" data-test="lot-create-option">
          <span class="lot-create-label">Create lot {{ option.lot_code }}</span>
          <span class="tag is-warning is-light">New</span>
        </div>
        <div v-else class="lot-option">
          <span class="lot-option-name">
            <span class="lot-option-code" data-test="lot-option-code">{{ option.lot_code }}</span>
            <span
              v-if="inUseValues.includes(option.value)"
              class="tag is-info is-light"
              data-test="lot-option-in-use"
            >
              {{ inUseLabel }}
            </span>
          </span>
          <span class="lot-option-meta">
            <span
              v-if="option.date"
              class="lot-option-date has-text-grey"
              data-test="lot-option-date"
            >
              {{ option.date }}
            </span>
            <span
              v-if="option.remaining"
              class="lot-option-remaining has-text-grey has-text-weight-medium"
              data-test="lot-option-remaining"
            >
              {{ option.remaining }}
            </span>
          </span>
        </div>
      </template>
    </BaseCombobox>

    <WrapWithHint v-if="isRequired" class="lot-badge" :content="requiredHint">
      <span class="tag is-danger is-light" data-test="lot-required-badge">Required</span>
    </WrapWithHint>
    <WrapWithHint v-else-if="isNew" class="lot-badge" :content="newLotHint">
      <span class="tag is-warning is-light" data-test="lot-new-badge">New</span>
    </WrapWithHint>
    <WrapWithHint v-else-if="isDuplicate" class="lot-badge" :content="duplicateHint">
      <span class="tag is-danger is-light" data-test="lot-duplicate-badge">Duplicate</span>
    </WrapWithHint>
    <WrapWithHint v-else-if="showNegativeBadge" class="lot-badge" :content="negativeHint">
      <span class="tag is-danger is-light" data-test="lot-negative-badge">Negative</span>
    </WrapWithHint>
  </div>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .combobox-more-results {
    padding: 0.375rem 1rem;
    font-size: 0.875em;
    cursor: default;
    position: sticky;
    bottom: 0;
    background: $white;
    border-top: 1px solid $grey-lighter;
  }

  .lot-code-combobox {
    position: relative;
    width: 100%;

    :deep(.combobox-dropdown) {
      min-width: 17rem;
    }

    &.has-badge :deep(.control.has-icons-right .input) {
      padding-right: 5.25rem;
    }
  }

  // The inventory views force every .tag to a large, fully-rounded pill on mobile
  // (shared_inventory.scss, body.inventory-view @media max-width: 767px). These
  // !important overrides keep the lot badges compact across breakpoints.
  .lot-badge .tag,
  .lot-create .tag,
  .lot-option .tag {
    font-size: 0.65rem !important;
    height: auto !important;
    line-height: 1.5 !important;
    padding: 0.15em 0.4em !important;
    border-radius: $radius !important;
  }

  .lot-badge {
    position: absolute;
    right: 1.9rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
  }

  .lot-option {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
  }

  .lot-option-name {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
  }

  .lot-option-code {
    font-weight: 600;
  }

  .lot-option-meta {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.1rem 0.5rem;
    font-size: 0.8rem;
    text-align: right;
  }

  .lot-option-remaining {
    white-space: nowrap;
  }

  .lot-create {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
  }

  .lot-create-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
